import type { CSSProperties, FC } from 'react';
import { memo, useCallback, useMemo } from 'react';
import { addSportFavoritesV2Request, removeSportFavoritesV2Request, selectCouponSelectedBetsV2, selectIsCouponQuickBetsV2, selectUserIsAuthenticated } from '@mwl/core-lib';
import type { OutcomeButtonBaseOldClasses } from '@mwl/ui';
import { OutcomeButton } from '@/components/OutcomeButton/OutcomeButton';
import { useMediaWidthDown, useTypedDispatch, useTypedSelector } from '@/hooks';
import { ButtonFavorites } from '../ButtonFavorites/ButtonFavorites';
import { MarketDescription } from './components/MarketDescription/MarketDescription';
import { getAnalyticsDataForFavorites, getAnalyticsMarkets } from './MarketBlock.analytics';
import type { MarketBlockProps } from './MarketBlock.types';
import { convertOutcomesToMatrix, formatMarketRow } from './MarketBlock.utils';
import styles from './MarketBlock.module.scss';
const outcomeClasses: OutcomeButtonBaseOldClasses = {
  frozen: styles.frozen,
  title: styles.outcomeTitle,
  value: styles.outcomeValue
};
const BaseMarketBlock: FC<MarketBlockProps> = ({
  marketTitle,
  lineId,
  linePaused,
  outcomes,
  marketId,
  isFavorite,
  description,
  variant,
  lineStage,
  category,
  championships,
  matchId,
  sportCategory
}) => {
  const dispatch = useTypedDispatch();
  const isSmallScreen = useMediaWidthDown('screen850');
  const is1100Screen = useMediaWidthDown('screen1100');
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const selectedBets = useTypedSelector(selectCouponSelectedBetsV2);
  const isQuickBets = useTypedSelector(selectIsCouponQuickBetsV2);
  const currentAnalyticaFavorite = isFavorite ? getAnalyticsDataForFavorites(lineId, marketId, marketTitle).lineInfo_market_delFavorite_icon : getAnalyticsDataForFavorites(lineId, marketId, marketTitle).lineInfo_market_addFavorite_icon;
  const isOutright = variant === 'OUTRIGHT';
  const colCount = useMemo(() => {
    if (isOutright && !is1100Screen) {
      return 4;
    }
    return 2;
  }, [is1100Screen, isOutright]);
  const groupedOdds = useMemo(() => {
    if (outcomes.length === 3) {
      return [formatMarketRow(outcomes)];
    }
    return convertOutcomesToMatrix(outcomes, colCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmallScreen, outcomes, variant, colCount]);
  const toggleFavorite = useCallback(async (newValue: boolean) => {
    if (newValue) {
      dispatch(addSportFavoritesV2Request({
        marketIds: [marketId]
      }));
    } else {
      dispatch(removeSportFavoritesV2Request({
        marketIds: [marketId]
      }));
    }
  }, [dispatch, marketId]);
  if (outcomes.length === 0) {
    return null;
  }
  return <div className={cn(styles.root, variant && styles[variant.toLowerCase()])} data-sentry-component="BaseMarketBlock" data-sentry-source-file="MarketBlock.tsx">
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <h4 className={styles.title}>{marketTitle}</h4>
          {isOutright && <MarketDescription description={description} />}
        </div>
        {isAuth && !isOutright && <ButtonFavorites size="xs" color="red" className={cn(styles.favorites, currentAnalyticaFavorite?.click?.data?.cls)} onClick={toggleFavorite} value={isFavorite} analytics={currentAnalyticaFavorite} variant="outlined" />}
      </div>
      <div className={styles.outcomes}>
        {groupedOdds.map(row => <div className={styles.outcomesRow} key={row.outcomes.reduce((id, outcome) => id + outcome.id + outcome.status, '')} style={({
        '--outcomes-count': isOutright ? colCount : row.outcomes.length
      } as CSSProperties)}>
            {row.outcomes.map(outcome => {
          const isSelected = isQuickBets ? false : !!selectedBets.find(bet => bet.outcomeId === outcome?.id);
          const analyticsParams = {
            line_id: lineId,
            outcomes_id: outcome.id,
            odds: outcome.odds,
            text: outcome?.title,
            isOutright,
            category,
            championships,
            matchId,
            sportCategory
          };
          const currentAnalytica = isSelected ? getAnalyticsMarkets(analyticsParams).unselect : getAnalyticsMarkets(analyticsParams).select;
          return <OutcomeButton key={outcome.id} outcome={outcome} variant="horizontal" isFrozen={outcome.status === 'PAUSED' || linePaused} className={cn(styles.outcomeButton, currentAnalytica?.click?.data?.cls, {
            [styles.outrightOutcomeButton]: isOutright
          })} classes={outcomeClasses} lineId={lineId} lineStage={lineStage} animationTimeout={1000} analytics={currentAnalytica} />;
        })}
          </div>)}
      </div>
    </div>;
};
export const MarketBlock = memo(BaseMarketBlock);
MarketBlock.displayName = 'MarketBlock';